import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  message,
  Spin,
  Input,
  Tooltip,
} from 'antd';
import DatePicker from '../DatePicker';
import {
  ScissorOutlined,
  FileTextOutlined,
  PlusOutlined,
  UserOutlined,
  DeleteOutlined,
  WarningOutlined,
  BulbOutlined,
  LeftOutlined,
  RightOutlined,
  EyeOutlined,
  CheckCircleFilled,
  CloseOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import DraggableModal from '../modal/DraggableModal';
import LoadingMask from '../LoadingMask';
import { hideModal } from '../../actions/modal';
import { getRecordById } from '../../actions/records';
import { performAiSplit, fetchAutoSplitSuggestions } from '../../actions/autoSplit';
import AutoSplitPDFPreview from './AutoSplitPDFPreview';
import {
  trackAutoSplitModalClosed,
  trackAutoSplitExecuted,
  trackAutoSplitSucceeded,
  trackAutoSplitFailed,
  trackAutoSplitPartAdded,
  trackAutoSplitPartRemoved,
} from '../../global/analytics';
import './autoSplit.scss';

const formatShortPageRange = (pageNumbers) => {
  if (!pageNumbers || pageNumbers.length === 0) return '';
  if (pageNumbers.length === 1) return `${pageNumbers[0]}`;

  const sorted = [...pageNumbers].sort((a, b) => a - b);
  const ranges = [];
  let rangeStart = sorted[0];
  let rangeEnd = sorted[0];

  for (let i = 1; i <= sorted.length; i++) {
    if (i < sorted.length && sorted[i] === rangeEnd + 1) {
      rangeEnd = sorted[i];
    } else {
      if (rangeStart === rangeEnd) {
        ranges.push(`${rangeStart}`);
      } else {
        ranges.push(`${rangeStart}-${rangeEnd}`);
      }
      if (i < sorted.length) {
        rangeStart = sorted[i];
        rangeEnd = sorted[i];
      }
    }
  }

  return ranges.join(', ');
};

const parsePageInput = (input) => {
  const pageNumbers = [];
  let hasInvalidFormat = false;
  const parts = input.split(',').map(p => p.trim()).filter(Boolean);

  parts.forEach(p => {
    if (p.includes('-')) {
      const rangeParts = p.split('-');
      if (rangeParts.length !== 2) {
        hasInvalidFormat = true;
        return;
      }
      const [startStr, endStr] = rangeParts;
      const start = parseInt(startStr.trim(), 10);
      const end = parseInt(endStr.trim(), 10);
      if (!isNaN(start) && !isNaN(end) && start <= end && start > 0) {
        for (let i = start; i <= end; i++) {
          if (!pageNumbers.includes(i)) pageNumbers.push(i);
        }
      } else {
        hasInvalidFormat = true;
      }
    } else {
      const num = parseInt(p, 10);
      if (!isNaN(num) && num > 0 && !pageNumbers.includes(num)) {
        pageNumbers.push(num);
      } else if (isNaN(num) || num <= 0) {
        hasInvalidFormat = true;
      }
    }
  });

  if (hasInvalidFormat) {
    return { pages: pageNumbers.sort((a, b) => a - b), hasInvalidFormat: true };
  }

  return { pages: pageNumbers.sort((a, b) => a - b), hasInvalidFormat: false };
};

const safeParseDob = (dobString) => {
  if (!dobString || typeof dobString !== 'string') return null;
  const parsed = moment(dobString, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed : null;
};

const AutoSplitModal = ({
  modalType,
  hideModal: dispatchHideModal,
  performAiSplit: dispatchPerformAiSplit,
  getRecordById: dispatchGetRecordById,
  fetchAutoSplitSuggestions: dispatchFetchSuggestions,
  suggestion,
  isLoading,
  downloadUrl,
}) => {
  const { sendToken, record } = modalType?.data || {};
  const originalParts = suggestion?.parts || [];
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  // Store signed URL locally to prevent Redux changes from affecting the preview
  const [localSignedUrl, setLocalSignedUrl] = useState(null);
  // Track when we're waiting for a fresh URL (to prevent syncing stale URL from Redux)
  const [isAwaitingFreshUrl, setIsAwaitingFreshUrl] = useState(false);
  // Cached PDF as ArrayBuffer - loaded once, used for all split previews
  const [pdfArrayBuffer, setPdfArrayBuffer] = useState(null);
  const [isFetchingPdf, setIsFetchingPdf] = useState(false);
  const [pdfFetchError, setPdfFetchError] = useState(null);

  // Calculate total pages - use actual document page count as authoritative source
  // Use Math.max(0, ...) to avoid -Infinity when all parts have empty pages arrays
  const maxPageFromOriginalParts = originalParts.length > 0
    ? Math.max(0, ...originalParts.flatMap(p => p.pages || []))
    : 0;
  const documentPageCount = record?.num_pages || record?.numPages || 0;
  const originalTotalPages = Math.max(maxPageFromOriginalParts, documentPageCount);

  // Editable parts - initialized from suggestion
  const [editableParts, setEditableParts] = useState(originalParts);

  // Fetch split suggestions if not already loaded
  useEffect(() => {
    if (sendToken && !suggestion && !isLoading) {
      const numPages = record?.num_pages || record?.numPages || 0;
      dispatchFetchSuggestions(sendToken, numPages);
    }
  }, [sendToken, suggestion, isLoading, record?.num_pages, record?.numPages, dispatchFetchSuggestions]);

  // Update editable parts and selected parts when suggestion changes
  useEffect(() => {
    if (originalParts.length > 0 && editableParts.length === 0) {
      setEditableParts(originalParts);
      setSelectedParts(originalParts.map(() => true));
    }
  }, [originalParts]);

  // Fetch a fresh signed URL from the server
  const refreshSignedUrl = useCallback(async () => {
    const fileToken = record?.file_token;
    if (!fileToken || isFetchingUrl) return null;

    setIsFetchingUrl(true);
    try {
      await dispatchGetRecordById(fileToken, false, false, false);
      // The new URL will be available via Redux props on next render
      return true;
    } catch (error) {
      console.error('[AutoSplitModal] Failed to refresh signed URL:', error);
      return false;
    } finally {
      setIsFetchingUrl(false);
    }
  }, [record?.file_token, isFetchingUrl, dispatchGetRecordById]);

  // Capture the signed URL when available from Redux
  // Skip if we're awaiting a fresh URL (to prevent restoring stale expired URL)
  useEffect(() => {
    if (isAwaitingFreshUrl) return;
    if (downloadUrl && downloadUrl !== localSignedUrl) {
      setLocalSignedUrl(downloadUrl);
    }
  }, [downloadUrl, localSignedUrl, isAwaitingFreshUrl]);

  // Fetch the document URL if not already available
  useEffect(() => {
    const fileToken = record?.file_token;
    if (!localSignedUrl && !downloadUrl && fileToken && !isFetchingUrl) {
      setIsFetchingUrl(true);
      dispatchGetRecordById(fileToken, false, false, false)
        .finally(() => setIsFetchingUrl(false));
    }
  }, [downloadUrl, localSignedUrl, record?.file_token, dispatchGetRecordById, isFetchingUrl]);

  // Fetch PDF as ArrayBuffer once we have a valid signed URL
  // This caches the PDF locally so split previews are instant and immune to URL expiry
  useEffect(() => {
    const fetchPdfBlob = async () => {
      // Skip if we already have the PDF cached or are currently fetching
      if (pdfArrayBuffer || isFetchingPdf) return;

      // Need a signed URL to fetch
      if (!localSignedUrl) return;

      setIsFetchingPdf(true);
      setPdfFetchError(null);

      try {
        const response = await fetch(localSignedUrl);
        if (!response.ok) {
          // URL might have expired, try to refresh and retry
          if (response.status === 403 || response.status === 401) {
            const refreshed = await refreshSignedUrl();
            if (refreshed) {
              // Will retry on next effect run with new URL
              return;
            }
          }
          throw new Error(`Failed to fetch PDF: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        setPdfArrayBuffer(arrayBuffer);
      } catch (error) {
        console.error('[AutoSplitModal] Error fetching PDF:', error);
        setPdfFetchError('Failed to load document. Please try again.');
      } finally {
        setIsFetchingPdf(false);
      }
    };

    fetchPdfBlob();
  }, [localSignedUrl, pdfArrayBuffer, isFetchingPdf, refreshSignedUrl]);

  // Handler to retry PDF fetch after an error
  const handleRetryPdfFetch = useCallback(async () => {
    setPdfFetchError(null);
    setPdfArrayBuffer(null);
    // Mark that we're waiting for a fresh URL (prevents sync effect from restoring stale URL)
    setIsAwaitingFreshUrl(true);
    setLocalSignedUrl(null);
    await refreshSignedUrl();
    // Reset flag after refresh completes - new URL will come via Redux
    setIsAwaitingFreshUrl(false);
  }, [refreshSignedUrl]);

  const [selectedParts, setSelectedParts] = useState(
    () => originalParts.map(() => true),
  );
  const [isSplitting, setIsSplitting] = useState(false);
  const [activeSplitIndex, setActiveSplitIndex] = useState(0);
  const [showOriginal, setShowOriginal] = useState(false);
  const hadEditsRef = useRef(false);

  // Local state for current split's editable fields
  const currentPart = editableParts[activeSplitIndex] || {};
  const primaryPatient = (!currentPart.is_mixed_phi && currentPart.patients?.length === 1)
    ? currentPart.patients[0]
    : null;

  const [localTitle, setLocalTitle] = useState(currentPart.title || '');
  const [localPages, setLocalPages] = useState(formatShortPageRange(currentPart.pages));
  const [localFirstName, setLocalFirstName] = useState(primaryPatient?.first_name || '');
  const [localMiddleName, setLocalMiddleName] = useState(primaryPatient?.middle_name || '');
  const [localLastName, setLocalLastName] = useState(primaryPatient?.last_name || '');
  const [localDob, setLocalDob] = useState(primaryPatient?.dob || '');
  const [pagesError, setPagesError] = useState('');

  // Sync local state when active split changes
  useEffect(() => {
    const part = editableParts[activeSplitIndex] || {};
    const patient = (!part.is_mixed_phi && part.patients?.length === 1) ? part.patients[0] : null;
    setLocalTitle(part.title || '');
    // Use raw input if there's an error, otherwise format the pages
    if (part.pagesError && part.rawPagesInput !== null && part.rawPagesInput !== undefined) {
      setLocalPages(part.rawPagesInput);
      // Re-validate to show error message
      const { error } = validateAndParsePages(part.rawPagesInput);
      setPagesError(part.rawPagesInput === '' ? 'Pages are required' : error);
    } else {
      setLocalPages(formatShortPageRange(part.pages));
      setPagesError('');
    }
    setLocalFirstName(patient?.first_name || '');
    setLocalMiddleName(patient?.middle_name || '');
    setLocalLastName(patient?.last_name || '');
    setLocalDob(patient?.dob || '');
  }, [activeSplitIndex, editableParts]);

  const handlePartToggle = useCallback((index) => {
    setSelectedParts((prev) => {
      const newSelected = [...prev];
      newSelected[index] = !newSelected[index];
      return newSelected;
    });
  }, []);

  const handleUpdatePart = useCallback((index, updatedPart) => {
    hadEditsRef.current = true;
    setEditableParts((prev) => {
      const newParts = [...prev];
      newParts[index] = updatedPart;
      return newParts;
    });
  }, []);

  const handleAddPart = useCallback(() => {
    hadEditsRef.current = true;
    const newPart = {
      pages: [],
      title: '',
      patients: [],
      reasoning: '',
      is_mixed_phi: false,
    };
    setEditableParts((prev) => [...prev, newPart]);
    setSelectedParts((prev) => [...prev, true]);
    setActiveSplitIndex(editableParts.length);
    setShowOriginal(false);
    trackAutoSplitPartAdded({
      sendToken,
      totalPages: originalTotalPages,
      partsCount: editableParts.length + 1,
    });
  }, [editableParts.length, sendToken, originalTotalPages]);

  const handleRemovePart = useCallback((index) => {
    hadEditsRef.current = true;
    trackAutoSplitPartRemoved({
      sendToken,
      totalPages: originalTotalPages,
      partsCount: editableParts.length - 1,
      removedIndex: index,
    });
    setEditableParts((prev) => prev.filter((_, i) => i !== index));
    setSelectedParts((prev) => prev.filter((_, i) => i !== index));
    setActiveSplitIndex((prev) => {
      if (prev >= index && prev > 0) return prev - 1;
      return prev;
    });
  }, [sendToken, originalTotalPages, editableParts.length]);

  // Input handlers
  const validateAndParsePages = (input) => {
    const { pages: parsed, hasInvalidFormat } = parsePageInput(input);

    if (hasInvalidFormat) {
      return { valid: false, pages: parsed, error: 'Invalid format (use: 1-3, 5, 7)' };
    }

    if (parsed.length === 0) {
      return { valid: false, pages: [], error: 'Enter valid page numbers' };
    }

    const totalPages = originalTotalPages || 0;
    if (totalPages > 0) {
      const invalidPages = parsed.filter(p => p > totalPages || p < 1);
      if (invalidPages.length > 0) {
        return {
          valid: false,
          pages: parsed,
          error: `Invalid: ${invalidPages.join(', ')} (max ${totalPages})`,
        };
      }
    }

    return { valid: true, pages: parsed, error: '' };
  };

  const handleTitleBlur = () => {
    if (localTitle !== (currentPart.title || '')) {
      handleUpdatePart(activeSplitIndex, { ...currentPart, title: localTitle });
    }
  };

  const handlePagesChange = (e) => {
    const input = e.target.value;
    setLocalPages(input);
    if (input.trim()) {
      const { error } = validateAndParsePages(input);
      setPagesError(error);
    } else {
      setPagesError('');
    }
  };

  const handlePagesBlur = () => {
    const { valid, pages: parsed, error } = validateAndParsePages(localPages);
    if (valid) {
      setPagesError('');
      handleUpdatePart(activeSplitIndex, { ...currentPart, pages: parsed, pagesError: false, rawPagesInput: null });
    } else if (localPages.trim() === '') {
      setPagesError('Pages are required');
      handleUpdatePart(activeSplitIndex, { ...currentPart, pages: [], pagesError: true, rawPagesInput: '' });
    } else {
      // Keep the invalid input visible - store raw input on part
      setPagesError(error);
      handleUpdatePart(activeSplitIndex, { ...currentPart, pagesError: true, rawPagesInput: localPages });
    }
  };

  const handlePatientFieldBlur = (field, value) => {
    const currentPatient = (currentPart.patients?.length > 0) ? currentPart.patients[0] : {};
    const updatedPatient = {
      first_name: field === 'first_name' ? value : (currentPatient.first_name || localFirstName),
      middle_name: field === 'middle_name' ? value : (currentPatient.middle_name || localMiddleName),
      last_name: field === 'last_name' ? value : (currentPatient.last_name || localLastName),
      dob: field === 'dob' ? value : (currentPatient.dob || localDob),
    };
    handleUpdatePart(activeSplitIndex, {
      ...currentPart,
      patients: [updatedPatient],
      is_mixed_phi: false,
    });
  };

  const selectedCount = useMemo(
    () => selectedParts.filter(s => s).length,
    [selectedParts],
  );

  // Detect overlapping pages across selected splits only
  const { overlappingPages, splitsWithOverlaps } = useMemo(() => {
    const pageToSplits = new Map(); // page -> [splitIndex1, splitIndex2, ...]

    editableParts.forEach((part, index) => {
      // Only check selected splits for overlaps
      if (!selectedParts[index]) return;

      (part.pages || []).forEach(page => {
        if (!pageToSplits.has(page)) {
          pageToSplits.set(page, []);
        }
        pageToSplits.get(page).push(index);
      });
    });

    // Find pages that appear in multiple selected splits
    const overlaps = new Map();
    const affectedSplits = new Set();

    pageToSplits.forEach((splitIndices, page) => {
      if (splitIndices.length > 1) {
        overlaps.set(page, splitIndices);
        splitIndices.forEach(idx => affectedSplits.add(idx));
      }
    });

    return {
      overlappingPages: overlaps,
      splitsWithOverlaps: affectedSplits,
    };
  }, [editableParts, selectedParts]);

  // Get overlapping pages for a specific split
  const getOverlapsForSplit = useCallback((splitIndex) => {
    const part = editableParts[splitIndex];
    if (!part?.pages) return [];

    return part.pages.filter(page => overlappingPages.has(page));
  }, [editableParts, overlappingPages]);

  // Validate splits before submission
  // originalIndices maps positions in partsToValidate back to their original indices in editableParts
  const validateSplits = useCallback((partsToValidate, originalIndices = null) => {
    // Helper to get display number (1-indexed) for a part
    const getDisplayNum = (i) => (originalIndices ? originalIndices[i] : i) + 1;

    for (let i = 0; i < partsToValidate.length; i++) {
      const part = partsToValidate[i];
      if (part.pagesError) {
        message.error(`Split ${getDisplayNum(i)} has invalid page input. Please fix the pages field.`);
        return false;
      }
    }

    for (let i = 0; i < partsToValidate.length; i++) {
      const part = partsToValidate[i];
      if (!part.pages || part.pages.length === 0) {
        message.error(`Split ${getDisplayNum(i)} has no pages assigned. Please specify pages for all splits.`);
        return false;
      }
    }

    if (originalTotalPages > 0) {
      for (let i = 0; i < partsToValidate.length; i++) {
        const part = partsToValidate[i];
        const invalidPages = part.pages.filter(p => p > originalTotalPages || p < 1);
        if (invalidPages.length > 0) {
          message.error(`Split ${getDisplayNum(i)} has invalid pages (${invalidPages.join(', ')}). Document only has ${originalTotalPages} pages.`);
          return false;
        }
      }
    }

    const allPages = new Map();
    for (let i = 0; i < partsToValidate.length; i++) {
      const part = partsToValidate[i];
      for (const page of part.pages) {
        if (allPages.has(page)) {
          const existingIndex = allPages.get(page);
          message.error(`Page ${page} is assigned to multiple splits (Split ${getDisplayNum(existingIndex)} and Split ${getDisplayNum(i)}). Each page can only be in one split.`);
          return false;
        }
        allPages.set(page, i);
      }
    }

    return true;
  }, [originalTotalPages]);

  const handleAcceptSelected = async () => {
    const originalIndices = [];
    const partsToSplit = editableParts.filter((_, index) => {
      if (selectedParts[index]) {
        originalIndices.push(index);
        return true;
      }
      return false;
    });

    if (partsToSplit.length === 0) {
      message.warning('Please select at least one part to split.');
      return;
    }

    if (partsToSplit.length === editableParts.length) {
      await handleAcceptAll();
      return;
    }

    if (!validateSplits(partsToSplit, originalIndices)) {
      return;
    }

    const trackProps = {
      sendToken,
      totalPages: originalTotalPages,
      partsCount: editableParts.length,
      selectedCount: partsToSplit.length,
      splitMode: 'selected',
      hadEdits: hadEditsRef.current,
    };

    trackAutoSplitExecuted(trackProps);
    setIsSplitting(true);

    try {
      const result = await dispatchPerformAiSplit(sendToken, partsToSplit);

      if (!result.success) {
        trackAutoSplitFailed({ ...trackProps, error: result.error });
        message.error(`Failed to split document: ${result.error}`);
      } else {
        trackAutoSplitSucceeded(trackProps);
        message.success('Document split successfully. Check your inbox for the new records.');
        dispatchHideModal();
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.error || error?.message || 'Unknown error';
      trackAutoSplitFailed({ ...trackProps, error: errorMsg });
      message.error(`Failed to split document: ${errorMsg}`);
    } finally {
      setIsSplitting(false);
    }
  };

  const handleAcceptAll = async () => {
    if (editableParts.length === 0) {
      message.warning('Please add at least one part to split.');
      return;
    }

    if (!validateSplits(editableParts)) {
      return;
    }

    const trackProps = {
      sendToken,
      totalPages: originalTotalPages,
      partsCount: editableParts.length,
      selectedCount: editableParts.length,
      splitMode: 'all',
      hadEdits: hadEditsRef.current,
    };

    trackAutoSplitExecuted(trackProps);
    setSelectedParts(editableParts.map(() => true));
    setIsSplitting(true);

    try {
      const result = await dispatchPerformAiSplit(sendToken, editableParts);

      if (!result.success) {
        trackAutoSplitFailed({ ...trackProps, error: result.error });
        message.error(`Failed to split document: ${result.error}`);
      } else {
        trackAutoSplitSucceeded(trackProps);
        message.success('Document split successfully. Check your inbox for the new records.');
        dispatchHideModal();
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.error || error?.message || 'Unknown error';
      trackAutoSplitFailed({ ...trackProps, error: errorMsg });
      message.error(`Failed to split document: ${errorMsg}`);
    } finally {
      setIsSplitting(false);
    }
  };

  const handleClose = () => {
    trackAutoSplitModalClosed({
      sendToken,
      totalPages: originalTotalPages,
      partsCount: editableParts.length,
      selectedCount: selectedParts.filter(s => s).length,
      hadEdits: hadEditsRef.current,
    });
    dispatchHideModal();
  };

  const maxPageFromParts = editableParts.length > 0
    ? Math.max(0, ...editableParts.flatMap(p => p.pages || []))
    : 0;
  const totalPages = originalTotalPages || maxPageFromParts;

  const handlePrevSplit = () => {
    if (activeSplitIndex > 0) {
      setActiveSplitIndex(activeSplitIndex - 1);
      setShowOriginal(false);
    }
  };

  const handleNextSplit = () => {
    if (activeSplitIndex < editableParts.length - 1) {
      setActiveSplitIndex(activeSplitIndex + 1);
      setShowOriginal(false);
    }
  };

  // New Design - Split Document Modal
  return (
    <DraggableModal
      visible
      title={null}
      onCancel={handleClose}
      width={1400}
      centered
      className="auto-split-modal auto-split-modal-main"
      bodyStyle={{ height: '85vh', overflow: 'hidden', padding: 0 }}
      footer={null}
      closable={false}
      handleClass=".split-header"
    >
      {isLoading || isFetchingUrl || isFetchingPdf ? (
        <div className="split-loading">
          <div className="split-loading-content">
            <div className="split-loading-icon">
              <ScissorOutlined />
            </div>
            <Spin size="large" />
            <span className="split-loading-text">
              {isFetchingPdf ? 'Loading document preview...' : isFetchingUrl ? 'Loading document...' : 'AI is analyzing your document...'}
            </span>
          </div>
        </div>
      ) : pdfFetchError ? (
        <div className="split-loading">
          <div className="split-loading-content">
            <div className="split-loading-icon error">
              <WarningOutlined />
            </div>
            <span className="split-loading-text error">{pdfFetchError}</span>
            <Button type="primary" onClick={handleRetryPdfFetch} style={{ marginTop: 16 }}>
              Retry
            </Button>
          </div>
        </div>
      ) : (
        <div className="split-layout">
          {/* Loading overlay when splitting */}
          <LoadingMask loading={isSplitting} />
          {/* Header */}
          <div className="split-header">
            <div className="split-header-left">
              <div className="split-logo">
                <ScissorOutlined />
              </div>
              <div className="split-title-group">
                <h1 className="split-title">Split Document</h1>
                <span className="split-subtitle">
                  {totalPages} pages &bull; {editableParts.length} splits detected
                </span>
              </div>
            </div>
            <button type="button" className="split-close-btn" onClick={handleClose}>
              <CloseOutlined />
            </button>
          </div>

          {/* Main Content - Three Column Layout */}
          <div className="split-content">
            {/* Left Sidebar - Document Splits */}
            <div className="split-sidebar">
              <div className="split-sidebar-header">
                <span>Document Splits</span>
                <button type="button" className="split-add-btn" onClick={handleAddPart}>
                  <PlusOutlined /> Add Split
                </button>
              </div>

              <div className="split-sidebar-list">
                {/* Original Document Card */}
                <div
                  className={`split-doc-card original ${showOriginal ? 'active' : ''}`}
                  onClick={() => setShowOriginal(true)}
                >
                  <div className="split-doc-icon">
                    <FileTextOutlined />
                  </div>
                  <div className="split-doc-info">
                    <span className="split-doc-title">Original Document</span>
                    <span className="split-doc-meta">{totalPages} pages</span>
                  </div>
                  <div className="split-doc-view">
                    <EyeOutlined />
                  </div>
                </div>

                <div className="split-sidebar-divider">
                  <span>AI DETECTED SPLITS</span>
                </div>

                {/* Split Cards */}
                {editableParts.map((part, index) => {
                  const patient = (!part.is_mixed_phi && part.patients?.length === 1) ? part.patients[0] : null;
                  const patientName = patient ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim() : '';
                  const isActive = activeSplitIndex === index && !showOriginal;
                  const isSelected = selectedParts[index];
                  const hasOverlap = splitsWithOverlaps.has(index);
                  const overlapPages = hasOverlap ? getOverlapsForSplit(index) : [];

                  const cardContent = (
                    <div
                      key={index}
                      className={`split-card ${isActive ? 'active' : ''} ${isSelected ? '' : 'excluded'} ${hasOverlap ? 'has-overlap' : ''}`}
                      onClick={() => {
                        setActiveSplitIndex(index);
                        setShowOriginal(false);
                      }}
                    >
                      <div
                        className="split-card-select"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePartToggle(index);
                        }}
                      >
                        {isSelected ? (
                          <CheckCircleFilled className="checked" />
                        ) : (
                          <div className="unchecked" />
                        )}
                      </div>
                      <div className="split-card-info">
                        <div className="split-card-header">
                          <span className="split-card-name">Split {index + 1}</span>
                          {part.is_mixed_phi && <WarningOutlined className="split-warning-icon" />}
                          {hasOverlap && <WarningOutlined className="split-overlap-icon" />}
                        </div>
                        <div className="split-card-details">
                          <span className={`split-card-pages ${hasOverlap ? 'has-overlap' : ''}`}>
                            pg {formatShortPageRange(part.pages) || '—'}
                          </span>
                          {patientName && (
                            <span className="split-card-patient">
                              <UserOutlined /> {patientName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );

                  return hasOverlap ? (
                    <Tooltip
                      key={index}
                      title={`Page ${overlapPages.join(', ')} assigned to multiple splits`}
                      color="#fff1f0"
                      overlayInnerStyle={{ color: '#cf1322'}}
                      placement="right"
                    >
                      {cardContent}
                    </Tooltip>
                  ) : cardContent;
                })}
              </div>
            </div>

            {/* Center - Form Area */}
            <div className="split-form-area">
              {!showOriginal && (
                <>
                  {/* Split Navigation */}
                  <div className="split-form-nav">
                    <button
                      type="button"
                      className="split-nav-btn"
                      onClick={handlePrevSplit}
                      disabled={activeSplitIndex === 0}
                    >
                      <LeftOutlined />
                    </button>
                    <span className="split-nav-label">
                      Split {activeSplitIndex + 1} <span className="split-nav-total">of {editableParts.length}</span>
                    </span>
                    <button
                      type="button"
                      className="split-nav-btn"
                      onClick={handleNextSplit}
                      disabled={activeSplitIndex === editableParts.length - 1}
                    >
                      <RightOutlined />
                    </button>

                    <div className="split-nav-actions">
                      {currentPart.reasoning && (
                        <Tooltip title={currentPart.reasoning} placement="bottom">
                          <button type="button" className="split-action-link">
                            <BulbOutlined /> Why?
                          </button>
                        </Tooltip>
                      )}
                      {editableParts.length > 1 && (
                        <button
                          type="button"
                          className="split-delete-btn"
                          onClick={() => handleRemovePart(activeSplitIndex)}
                        >
                          <DeleteOutlined />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="split-form-content">
                    {/* Pages */}
                    <div className="split-field">
                      <label>
                        <FileTextOutlined /> Pages
                      </label>
                      <Input
                        value={localPages}
                        onChange={handlePagesChange}
                        onBlur={handlePagesBlur}
                        placeholder="1"
                        className={pagesError ? 'error' : ''}
                      />
                      {pagesError ? (
                        <span className="split-field-error">{pagesError}</span>
                      ) : (
                        <span className="split-field-hint">Enter page numbers or ranges</span>
                      )}
                    </div>

                    {/* Document Title */}
                    <div className="split-field">
                      <label>
                        <FileTextOutlined /> Document Title
                      </label>
                      <Input
                        value={localTitle}
                        onChange={(e) => setLocalTitle(e.target.value)}
                        onBlur={handleTitleBlur}
                        placeholder="Referral for Patient Name"
                      />
                    </div>

                    {/* Patient Information */}
                    {currentPart.is_mixed_phi ? (
                      <div className="split-warning-box">
                        <WarningOutlined />
                        <div>
                          <strong>Multiple patients detected</strong>
                          <p>This section may contain records for multiple patients. Review carefully.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="split-patient-section">
                        <label>
                          <UserOutlined /> Patient Information
                        </label>
                        <div className="split-patient-row">
                          <div className="split-patient-field">
                            <span className="split-patient-label">FIRST NAME</span>
                            <Input
                              value={localFirstName}
                              onChange={(e) => setLocalFirstName(e.target.value)}
                              onBlur={() => handlePatientFieldBlur('first_name', localFirstName)}
                              placeholder="First name"
                            />
                          </div>
                          <div className="split-patient-field">
                            <span className="split-patient-label">MIDDLE NAME</span>
                            <Input
                              value={localMiddleName}
                              onChange={(e) => setLocalMiddleName(e.target.value)}
                              onBlur={() => handlePatientFieldBlur('middle_name', localMiddleName)}
                              placeholder="Middle name"
                            />
                          </div>
                          <div className="split-patient-field">
                            <span className="split-patient-label">LAST NAME</span>
                            <Input
                              value={localLastName}
                              onChange={(e) => setLocalLastName(e.target.value)}
                              onBlur={() => handlePatientFieldBlur('last_name', localLastName)}
                              placeholder="Last name"
                            />
                          </div>
                        </div>
                        <div className="split-patient-field dob">
                          <span className="split-patient-label">DATE OF BIRTH</span>
                          <DatePicker
                            value={safeParseDob(localDob)}
                            onChange={(date) => {
                              const dateStr = date && moment.isMoment(date) && date.isValid()
                                ? date.format('YYYY-MM-DD')
                                : '';
                              setLocalDob(dateStr);
                              handlePatientFieldBlur('dob', dateStr);
                            }}
                            format="MM/DD/YYYY"
                            placeholder="Date of birth"
                            className="split-dob-picker"
                          />
                        </div>
                      </div>
                    )}

                  </div>
                </>
              )}

              {showOriginal && (
                <div className="split-original-view">
                  <div className="split-original-header">
                    <EyeOutlined />
                    <span>Viewing Original Document</span>
                    <span className="split-original-pages">{totalPages} pages</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right - Preview Panel */}
            <div className="split-preview-panel">
              <div className="split-preview-header">
                <span className="split-preview-title">Preview</span>
                <span className="split-preview-pages">
                  {showOriginal ? totalPages : (currentPart.pages?.length || 0)} pages
                </span>
              </div>
              <div className="split-preview-content">
                <AutoSplitPDFPreview
                  pdfArrayBuffer={pdfArrayBuffer}
                  pages={showOriginal ? null : currentPart.pages}
                  height="100%"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="split-footer">
            <Button className="split-btn-dismiss" onClick={handleClose}>
              Dismiss
            </Button>
            <div className="split-footer-actions">
              <Button
                className="split-btn-secondary"
                onClick={handleAcceptAll}
                disabled={isSplitting || editableParts.length === 0}
              >
                Split All ({editableParts.length})
              </Button>
              <Button
                type="primary"
                className="split-btn-primary"
                onClick={handleAcceptSelected}
                disabled={isSplitting || selectedCount === 0}
                loading={isSplitting}
                icon={<ScissorOutlined />}
              >
                Split Selected ({selectedCount})
              </Button>
            </div>
          </div>
        </div>
      )}
    </DraggableModal>
  );
};

AutoSplitModal.propTypes = {
  modalType: PropTypes.object.isRequired,
  hideModal: PropTypes.func.isRequired,
  performAiSplit: PropTypes.func.isRequired,
  getRecordById: PropTypes.func.isRequired,
  fetchAutoSplitSuggestions: PropTypes.func.isRequired,
  suggestion: PropTypes.object,
  isLoading: PropTypes.bool,
  downloadUrl: PropTypes.string,
};

AutoSplitModal.defaultProps = {
  suggestion: null,
  isLoading: false,
  downloadUrl: null,
};

const mapStateToProps = (state, ownProps) => {
  const sendToken = ownProps.modalType?.data?.sendToken;
  const recordFileToken = ownProps.modalType?.data?.record?.file_token;

  // Use currentRecord (the document URL) for the PDF preview
  // Only use the cached URL if it corresponds to the record we want to display
  const cachedFileToken = state.records?.fileToken;
  const isMatchingRecord = cachedFileToken === recordFileToken;
  const downloadUrl = isMatchingRecord
    ? (state.records?.currentRecord || state.records?.downloadUrl)
    : null;

  return {
    suggestion: state.autoSplit?.suggestions?.[sendToken],
    isLoading: state.autoSplit?.loading?.[sendToken] || false,
    downloadUrl,
  };
};

export default connect(mapStateToProps, {
  hideModal,
  performAiSplit,
  getRecordById,
  fetchAutoSplitSuggestions,
})(AutoSplitModal);
